import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import {
  dismissSessionAlerts,
  getPendingSessionAlerts,
  type SessionAlertRow,
} from '@/lib/authSessions'

export function NewSessionAlertBanner() {
  const { user } = useAuth()
  const [alerts, setAlerts] = useState<SessionAlertRow[]>([])
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!user) {
      setAlerts([])
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        const pending = await getPendingSessionAlerts()
        if (!cancelled) setAlerts(pending)
      } catch {
        if (!cancelled) setAlerts([])
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [user?.id])

  if (!user || dismissed || alerts.length === 0) return null

  const latest = alerts[0]
  const extraCount = alerts.length - 1

  const handleDismiss = async () => {
    setDismissed(true)
    try {
      await dismissSessionAlerts()
    } catch {
      /* banner já oculto localmente */
    }
  }

  return (
    <div
      role="alert"
      className="mx-3 sm:mx-4 md:mx-6 mt-3 mb-0 flex flex-col sm:flex-row sm:items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
        <div className="text-sm min-w-0">
          <p className="font-medium">Novo acesso à sua conta</p>
          <p className="text-amber-800/90 dark:text-amber-200/80 mt-0.5 break-words">
            Login detectado em <strong>{latest.device_label}</strong>
            {latest.ip_address ? ` (IP: ${latest.ip_address})` : ''}.
            {extraCount > 0 ? ` Mais ${extraCount} alerta(s) pendente(s).` : ''}
            {' '}
            Se não foi você, encerre as sessões em Configurações → Segurança.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 sm:ml-auto">
        <Button variant="outline" size="sm" asChild className="bg-white/80 dark:bg-transparent">
          <Link to="/settings" state={{ tab: 'seguranca' }}>
            Ver sessões
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label="Dispensar alerta"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
